import { useState } from "react";
import FetchData from "../../fetch-api/Apifetch";
import { SEND_VERIFICATION_CODE, VERIFY_EMAIL_CODE, UPDATE_LICENSE } from "../../utils/ApiRoute";
import "./../style/style.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { verifyOfflineCode } from "../../utils/LicenseCodes";
import { Modal } from "react-bootstrap";

const LicenseUpdate = () => {
  const [language] = useState("en");
  const [step, setStep] = useState(1); // Step 1: Send Code, Step 2: Verify Code, Step 3: Upload License
  const [buttonLoading, setButtonLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    verification_code: "",
  });
  const [licenseFile, setLicenseFile] = useState(null);
  const [licenseData, setLicenseData] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [verificationToken, setVerificationToken] = useState(null);
  const [isLocalIP, setIsLocalIP] = useState(false);

  const [showSupportModal, setShowSupportModal] = useState(false);
  const supportEmail = "support@yourcompany.com";
  const supportPhone = "+1 (800) 123-4567";

  const updateField = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const validateUserFields = () => {
    if (!formData.email || !formData.password) {
      toast.error("Email and password are required.");
      return false;
    }
    return true;
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    setUploadError("");
    setLicenseData(null);
    setLicenseFile(null);

    if (!file || file.type !== "application/json") {
      setUploadError("Please upload a valid .json file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);
        setLicenseData(json);
        setLicenseFile(file);
      } catch {
        setUploadError("Invalid JSON format.");
      }
    };
    reader.readAsText(file);
  };

  // Step 1 — Send Code
  const handleSendVerification = async () => {
    if (!validateUserFields()) return;
    setButtonLoading(true);
    try {
      const payload = {
        email: formData.email,
        password: formData.password,
      };
      const result = await FetchData(SEND_VERIFICATION_CODE, "POST", JSON.stringify(payload), false, false);

      if (result?.detail) {
        toast.success(result.detail || "Verification code sent!");
        if (result.detail.includes("Local IP detected")) {
          setIsLocalIP(true);
        } else {
          setIsLocalIP(false);
        }
        setStep(2);
      } else {
        toast.error(result?.detail || "Failed to send code.");
      }
    } catch (error) {
      toast.error("Error: " + (error.detail || "Unknown"));
    } finally {
      setButtonLoading(false);
    }
  };

  // Step 2 — Verify Code
  const handleVerifyCode = async () => {
    if (!formData.verification_code) {
      toast.error("Enter verification code");
      return;
    }
    setButtonLoading(true);
    try {
      if (isLocalIP) {
        // Perform frontend-side 16-digit code verification
        if (!verifyOfflineCode(formData.verification_code)) {
          toast.error("Invalid offline verification code.");
          setButtonLoading(false);
          return;
        }
      }
      const payload = {
        email: formData.email,
        verification_code: formData.verification_code,
      };
      const result = await FetchData(VERIFY_EMAIL_CODE, "POST", JSON.stringify(payload), false, false);

      if (result?.verification_token) {
        toast.success("Verification successful!");
        setVerificationToken(result.verification_token);
        setStep(3);
      } else {
        toast.error(result?.detail || "Invalid code.");
      }
    } catch (error) {
      toast.error("Error: " + (error.detail || "Unknown"));
    } finally {
      setButtonLoading(false);
    }
  };

  // Step 3 — Upload License
  const handleLicenseUpload = async () => {
    if (!licenseData) return toast.error("No license data to upload.");
    if (!verificationToken) return toast.error("Verification token missing.");

    setButtonLoading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append("verification_token", verificationToken);
      formDataObj.append("file", licenseFile, "license.json");

      const result = await FetchData(UPDATE_LICENSE, "POST", formDataObj, true, true);

      if (result?.detail) {
        toast.success(result.detail || "License uploaded successfully!");
        setFormData({ email: "", password: "", verification_code: "" });
        setLicenseData(null);
        setLicenseFile(null);
        setVerificationToken(null);
        setStep(1);

        // Auto-logout after successful update
        setTimeout(() => {
          const lang = localStorage.getItem("lang");
          localStorage.clear();
          if (lang) localStorage.setItem("lang", lang);
          window.location.href = "/admin-login";
        }, 2000);
      } else {
        toast.error(result?.detail || "License upload failed.");
      }
    } catch (error) {
      toast.error("Upload failed: " + (error.detail || "Unknown error"));
    } finally {
      setButtonLoading(false);
    }
  };

  const sendSupportEmail = () => {
    const subject = "License Support Request";
    const body = `Please describe your issue:\n\nOrganization:\nLicense ID:\nAdditional Details:`;
    window.location.href = `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="bg-teal-950" style={{ height: "100vh" }}>
      <div className="row h-100 justify-center p-10">
        <div className="col-lg-6 bg-teal-950 d-flex flex-column pt-3 h-100 align-items-center">
          <div className="container-fluid w-lg-75 p-1 mt-4 h-75">
            <div className="d-inline-flex w-100 justify-content-center mb-2">
              <img src={"/images/employeelogog.png"} style={{ width: "80px", height: "80px" }} alt="Login Logo" />
            </div>

            <div className="d-flex justify-content-center align-items-center flex-column row-gap-2">
              <span className="text-white bigheader">Update System License</span>
              <span className="text-white smallheader">Follow the 3-step process</span>
            </div>

            <div className="px-5 mt-4">
              {step === 1 && (
                <>
                  <label className="text-white">Admin Email</label>
                  <input type="email" className="form-control bg-secondary text-white inputs" name="email" value={formData.email} onChange={updateField} placeholder="Enter admin email" />
                  <label className="text-white mt-3">Admin Password</label>
                  <input type="password" className="form-control bg-secondary text-white inputs" name="password" value={formData.password} onChange={updateField} placeholder="Enter admin password" />
                  <button className="btn btn-danger w-100 mt-4" onClick={handleSendVerification} disabled={buttonLoading}>
                    {buttonLoading ? "Sending..." : "Send Verification Code"}
                  </button>
                </>
              )}

              {step === 2 && (
                <>
                  <label className="text-white">Verification Code</label>
                  <input type="text" className="form-control bg-secondary text-white inputs" name="verification_code" value={formData.verification_code} onChange={updateField} placeholder="Enter code" />
                  <button className="btn btn-primary w-100 mt-4" onClick={handleVerifyCode} disabled={buttonLoading}>
                    {buttonLoading ? "Verifying..." : "Verify Code"}
                  </button>
                </>
              )}

              {step === 3 && (
                <>
                  <label className="text-white">License File</label>
                  <input type="file" accept=".json" onChange={handleFileChange} className="form-control" style={{ backgroundColor: "#6c757d", color: "white" }} />
                  {uploadError && <div className="alert alert-danger mt-2">{uploadError}</div>}
                  <button className="btn btn-success w-100 mt-4" onClick={handleLicenseUpload} disabled={buttonLoading || !licenseData}>
                    {buttonLoading ? "Uploading..." : "Upload License"}
                  </button>
                </>
              )}

              <div className="mt-3 text-center">
                <span className="text-white smallheader" style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => setShowSupportModal(true)}>
                  Having issues? Contact support
                </span>
              </div>
            </div>

            <Modal show={showSupportModal} onHide={() => setShowSupportModal(false)} centered>
              <Modal.Header closeButton className="bg-dark text-white">
                <Modal.Title>License Support</Modal.Title>
              </Modal.Header>
              <Modal.Body className="bg-light">
                <h5 className="text-primary mb-4">We're here to help!</h5>
                <div className="mb-4">
                  <h6>Email Support</h6>
                  <p>{supportEmail}</p>
                  <button className="btn btn-sm btn-primary mt-2" onClick={sendSupportEmail}>Send Email</button>
                </div>
                <div>
                  <h6>Phone Support</h6>
                  <p>{supportPhone}</p>
                  <p className="small text-muted">Mon-Fri, 9AM-5PM EST</p>
                </div>
              </Modal.Body>
              <Modal.Footer className="bg-light">
                <button className="btn btn-secondary" onClick={() => setShowSupportModal(false)}>Close</button>
              </Modal.Footer>
            </Modal>

            <ToastContainer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseUpdate;
